import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminSidebar from '../components/layout/AdminSidebar';
import { Button } from '../components/ui/button';
import { 
  Search, 
  TrendingUp, 
  TrendingDown,
  Globe,
  Users,
  MousePointer,
  Eye,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ExternalLink,
  RefreshCw,
  MapPin,
  Target,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Info,
  Loader2,
  Building2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Target keywords for tracking
const TARGET_KEYWORDS = [
  { keyword: 'custom home builder concord nc', location: 'Concord', priority: 'high' },
  { keyword: 'custom home builder kannapolis nc', location: 'Kannapolis', priority: 'high' },
  { keyword: 'custom home builder davidson nc', location: 'Davidson', priority: 'high' },
  { keyword: 'custom home builder mooresville nc', location: 'Mooresville', priority: 'high' },
  { keyword: 'custom home builder china grove nc', location: 'China Grove', priority: 'high' },
  { keyword: 'home renovation contractor concord nc', location: 'Concord', priority: 'high' },
  { keyword: 'design build contractor nc', location: 'Regional', priority: 'medium' },
  { keyword: 'home additions cabarrus county', location: 'Cabarrus', priority: 'medium' },
  { keyword: 'whole home renovation lake norman', location: 'Lake Norman', priority: 'medium' },
];

// Competitor data
const COMPETITORS = [
  { name: 'PRESPRO Homes', website: 'presprohomes.com', type: 'Custom Homes' },
  { name: 'MDP Custom Homes', website: 'mdpcustomhomes.com', type: 'Custom Homes' },
  { name: 'Bespoke Homes', website: 'bespokehomesnc.com', type: 'Custom Homes' },
  { name: 'Artisan Custom Homes', website: 'artisancustomhomesnc.com', type: 'Custom Homes' },
  { name: 'Touchstone Custom Homes', website: 'touchstonecustomhomes.com', type: 'Custom Homes' },
];

// Location pages
const LOCATION_PAGES = [
  { slug: 'concord', city: 'Concord', county: 'Cabarrus' },
  { slug: 'kannapolis', city: 'Kannapolis', county: 'Cabarrus' },
  { slug: 'mooresville', city: 'Mooresville', county: 'Iredell' },
  { slug: 'davidson', city: 'Davidson', county: 'Mecklenburg' },
  { slug: 'china-grove', city: 'China Grove', county: 'Rowan' },
];

// Citation directories to get listed on
const CITATION_DIRECTORIES = [
  { name: 'Google Business Profile', url: 'https://business.google.com', priority: 'critical', status: 'claimed', category: 'Search' },
  { name: 'Yelp', url: 'https://biz.yelp.com', priority: 'high', status: 'not_claimed', category: 'Reviews' },
  { name: 'Facebook Business', url: 'https://business.facebook.com', priority: 'high', status: 'not_claimed', category: 'Social' },
  { name: 'Houzz', url: 'https://www.houzz.com/pro', priority: 'high', status: 'not_claimed', category: 'Home Services' },
  { name: 'Angi (Angie\'s List)', url: 'https://www.angi.com/pro', priority: 'high', status: 'not_claimed', category: 'Home Services' },
  { name: 'HomeAdvisor', url: 'https://pro.homeadvisor.com', priority: 'high', status: 'not_claimed', category: 'Home Services' },
  { name: 'Thumbtack', url: 'https://www.thumbtack.com/pro', priority: 'medium', status: 'not_claimed', category: 'Home Services' },
  { name: 'Better Business Bureau', url: 'https://www.bbb.org/get-accredited', priority: 'medium', status: 'not_claimed', category: 'Trust' },
  { name: 'Nextdoor Business', url: 'https://business.nextdoor.com', priority: 'medium', status: 'not_claimed', category: 'Local' },
  { name: 'Bing Places', url: 'https://www.bingplaces.com', priority: 'medium', status: 'not_claimed', category: 'Search' },
  { name: 'Apple Maps', url: 'https://mapsconnect.apple.com', priority: 'medium', status: 'not_claimed', category: 'Search' },
  { name: 'BuildZoom', url: 'https://www.buildzoom.com', priority: 'medium', status: 'not_claimed', category: 'Home Services' },
  { name: 'Porch', url: 'https://pro.porch.com', priority: 'low', status: 'not_claimed', category: 'Home Services' },
  { name: 'Yellow Pages', url: 'https://www.yellowpages.com', priority: 'low', status: 'not_claimed', category: 'Directory' },
];

const AdminSeoPage = () => {
  const navigate = useNavigate();
  const [seoData, setSeoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    if (isLoggedIn) {
      setIsAuthenticated(true);
      fetchSeoData();
    } else {
      navigate('/admin/login');
    }
    setCheckingAuth(false);
  }, [navigate]);

  const fetchSeoData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/seo/dashboard`);
      if (res.ok) {
        const data = await res.json();
        setSeoData(data);
      } else {
        // Use mock data if API not available yet
        setSeoData(getMockData());
      }
    } catch (error) {
      console.error('Error fetching SEO data:', error);
      setSeoData(getMockData());
    } finally {
      setLoading(false);
    }
  };

  const getMockData = () => ({
    healthScore: 78,
    lastUpdated: new Date().toISOString(),
    overview: {
      totalClicks: 0,
      totalImpressions: 0,
      avgPosition: 0,
      avgCtr: 0,
      trend: 'neutral',
      message: 'Connect Google Search Console to see real data'
    },
    technicalSeo: {
      score: 85,
      issues: [
        { type: 'warning', message: 'Some images missing alt text', count: 3 },
        { type: 'info', message: 'Consider adding more internal links', count: 0 },
      ],
      passed: [
        'Mobile-friendly design',
        'HTTPS enabled',
        'Sitemap present',
        'Robots.txt configured',
        'Structured data implemented',
        'Meta descriptions on all pages',
        'Canonical URLs set',
      ]
    },
    localSeo: {
      score: 72,
      googleBusinessProfile: 'not_connected',
      citations: {
        claimed: 0,
        total: 15,
        priority: ['Google Business Profile', 'Yelp', 'Facebook', 'Houzz', 'Angi']
      }
    },
    searchConsole: {
      connected: false,
      message: 'Connect Google Search Console for keyword rankings and traffic data'
    }
  });

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchSeoData();
    setRefreshing(false);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-amber-100';
    return 'bg-red-100';
  };

  if (checkingAuth || loading) {
    return (
      <AdminSidebar pageTitle="SEO Dashboard" pageIcon={Search}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </AdminSidebar>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <AdminSidebar pageTitle="SEO Dashboard" pageIcon={Search}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">SEO Dashboard</h1>
            <p className="text-slate-600">Monitor your search performance and optimize for local rankings</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">
              Last updated: {new Date(seoData?.lastUpdated).toLocaleString()}
            </span>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Connection Status Banner */}
        {!seoData?.searchConsole?.connected && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800">Connect Google Search Console for Real Data</h3>
                <p className="text-sm text-amber-700 mt-1">
                  To see actual keyword rankings, search traffic, and click-through rates, you need to connect your Google Search Console account.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Connect Search Console
                  </Button>
                  <Button size="sm" variant="outline" asChild>
                    <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer">
                      Open Search Console
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-slate-200">
          <div className="flex gap-6">
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3 },
              { id: 'keywords', label: 'Target Keywords', icon: Target },
              { id: 'locations', label: 'Location Pages', icon: MapPin },
              { id: 'citations', label: 'Citations', icon: Globe },
              { id: 'competitors', label: 'Competitors', icon: Building2 },
              { id: 'technical', label: 'Technical SEO', icon: Search },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-1 py-3 border-b-2 font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-cyan-600 text-cyan-600'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Health Score & Quick Stats */}
            <div className="grid md:grid-cols-4 gap-6">
              {/* SEO Health Score */}
              <div className={`${getScoreBg(seoData?.healthScore)} rounded-2xl p-6`}>
                <div className="text-sm font-medium text-slate-600 mb-2">SEO Health Score</div>
                <div className={`text-4xl font-bold ${getScoreColor(seoData?.healthScore)}`}>
                  {seoData?.healthScore}/100
                </div>
                <div className="text-sm text-slate-600 mt-2">
                  {seoData?.healthScore >= 80 ? 'Excellent' : seoData?.healthScore >= 60 ? 'Good' : 'Needs Work'}
                </div>
              </div>

              {/* Traffic Stats (placeholder until Search Console connected) */}
              <div className="bg-slate-50 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-slate-600">Search Clicks</div>
                  <MousePointer className="w-4 h-4 text-slate-400" />
                </div>
                <div className="text-3xl font-bold text-slate-900">
                  {seoData?.overview?.totalClicks || '—'}
                </div>
                <div className="text-sm text-slate-500 mt-1">Last 28 days</div>
              </div>

              <div className="bg-slate-50 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-slate-600">Impressions</div>
                  <Eye className="w-4 h-4 text-slate-400" />
                </div>
                <div className="text-3xl font-bold text-slate-900">
                  {seoData?.overview?.totalImpressions || '—'}
                </div>
                <div className="text-sm text-slate-500 mt-1">Last 28 days</div>
              </div>

              <div className="bg-slate-50 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-slate-600">Avg Position</div>
                  <TrendingUp className="w-4 h-4 text-slate-400" />
                </div>
                <div className="text-3xl font-bold text-slate-900">
                  {seoData?.overview?.avgPosition || '—'}
                </div>
                <div className="text-sm text-slate-500 mt-1">Google Search</div>
              </div>
            </div>

            {/* Action Items */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Priority Action Items</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                  <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-red-800">Claim Google Business Profile</div>
                    <div className="text-sm text-red-700">Critical for local SEO - this is how you appear in Google Maps and local search results</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-amber-800">Connect Google Search Console</div>
                    <div className="text-sm text-amber-700">Required to track keyword rankings and search performance</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-blue-800">Build Local Citations</div>
                    <div className="text-sm text-blue-700">Get listed on Houzz, Angi, Yelp, and other home services directories</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'keywords' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="p-6 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900">Target Keywords</h3>
                <p className="text-sm text-slate-600 mt-1">
                  These are the keywords you should be ranking for. Connect Search Console to see actual positions.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Keyword</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Location</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Priority</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Current Position</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Target</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {TARGET_KEYWORDS.map((kw, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="px-6 py-4">
                          <div className="font-medium text-slate-900">{kw.keyword}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center gap-1 text-slate-600">
                            <MapPin className="w-3 h-3" />
                            {kw.location}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            kw.priority === 'high' 
                              ? 'bg-red-100 text-red-700' 
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {kw.priority}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-slate-400">—</span>
                          <span className="text-xs text-slate-500 ml-2">(Connect Search Console)</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-green-600 font-medium">Top 3</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'locations' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Location Landing Pages</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Dedicated pages for each service area help you rank for local searches
                  </p>
                </div>
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                  {LOCATION_PAGES.length} Pages Live
                </span>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {LOCATION_PAGES.map((loc) => (
                  <div key={loc.slug} className="border border-slate-200 rounded-xl p-4 hover:border-cyan-300 transition-colors">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-slate-900">{loc.city}, NC</h4>
                        <p className="text-sm text-slate-500">{loc.county} County</p>
                      </div>
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" variant="outline" asChild>
                        <Link to={`/locations/${loc.slug}`}>
                          <Eye className="w-3 h-3 mr-1" />
                          View
                        </Link>
                      </Button>
                      <Button size="sm" variant="outline" asChild>
                        <a 
                          href={`https://www.google.com/search?q=custom+home+builder+${loc.city.toLowerCase()}+nc`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Search className="w-3 h-3 mr-1" />
                          Check Google
                        </a>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'citations' && (
          <div className="space-y-6">
            {/* Citation Stats */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-green-50 rounded-2xl p-6">
                <div className="text-sm font-medium text-slate-600 mb-2">Claimed</div>
                <div className="text-4xl font-bold text-green-600">
                  {CITATION_DIRECTORIES.filter(c => c.status === 'claimed').length}
                </div>
                <div className="text-sm text-slate-500">of {CITATION_DIRECTORIES.length} directories</div>
              </div>
              
              <div className="bg-amber-50 rounded-2xl p-6">
                <div className="text-sm font-medium text-slate-600 mb-2">High Priority Remaining</div>
                <div className="text-4xl font-bold text-amber-600">
                  {CITATION_DIRECTORIES.filter(c => c.status !== 'claimed' && c.priority === 'high').length}
                </div>
                <div className="text-sm text-slate-500">directories to claim</div>
              </div>
              
              <div className="bg-cyan-50 rounded-2xl p-6">
                <div className="text-sm font-medium text-slate-600 mb-2">Citation Score</div>
                <div className="text-4xl font-bold text-cyan-600">
                  {Math.round((CITATION_DIRECTORIES.filter(c => c.status === 'claimed').length / CITATION_DIRECTORIES.length) * 100)}%
                </div>
                <div className="text-sm text-slate-500">completion</div>
              </div>
            </div>

            {/* Citation Checklist */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Citation Directories</h3>
                  <p className="text-sm text-slate-600">Get listed on these sites to boost local SEO and build backlinks</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {CITATION_DIRECTORIES.map((dir, idx) => (
                  <div 
                    key={idx} 
                    className={`flex items-center justify-between p-4 rounded-xl border ${
                      dir.status === 'claimed' 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-white border-slate-200 hover:border-cyan-300'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        dir.status === 'claimed' ? 'bg-green-100' : 'bg-slate-100'
                      }`}>
                        {dir.status === 'claimed' ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        ) : (
                          <Globe className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">{dir.name}</div>
                        <div className="text-sm text-slate-500">{dir.category}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        dir.priority === 'critical' ? 'bg-red-100 text-red-700' :
                        dir.priority === 'high' ? 'bg-amber-100 text-amber-700' :
                        dir.priority === 'medium' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {dir.priority}
                      </span>
                      
                      {dir.status === 'claimed' ? (
                        <span className="text-sm text-green-600 font-medium">Claimed</span>
                      ) : (
                        <Button size="sm" variant="outline" asChild>
                          <a href={dir.url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-3 h-3 mr-1" />
                            Claim
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">NAP Consistency Tip</h4>
                <p className="text-sm text-blue-800">
                  When creating listings, always use the exact same Name, Address, and Phone number (NAP) format across all directories. 
                  Inconsistent NAP hurts local SEO rankings.
                </p>
                <div className="mt-3 p-3 bg-white rounded border border-blue-200 font-mono text-sm">
                  <div><strong>Name:</strong> C3 Contracting</div>
                  <div><strong>Address:</strong> Concord, NC</div>
                  <div><strong>Phone:</strong> (980) 680-6703</div>
                  <div><strong>Website:</strong> https://c3-contracting.com</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'competitors' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-6">Competitor Analysis</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Competitor</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Website</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Type</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {COMPETITORS.map((comp, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="px-6 py-4 font-medium text-slate-900">{comp.name}</td>
                        <td className="px-6 py-4">
                          <a 
                            href={`https://${comp.website}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-cyan-600 hover:underline inline-flex items-center gap-1"
                          >
                            {comp.website}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </td>
                        <td className="px-6 py-4 text-slate-600">{comp.type}</td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" asChild>
                              <a 
                                href={`https://www.google.com/search?q=site:${comp.website}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Search className="w-3 h-3 mr-1" />
                                Indexed Pages
                              </a>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium text-slate-900 mb-2">Pro Tip: Spy on Competitors</h4>
                <p className="text-sm text-slate-600">
                  Use free tools like <a href="https://neilpatel.com/ubersuggest/" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">Ubersuggest</a> or <a href="https://moz.com/free-seo-tools" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">Moz Free Tools</a> to see what keywords your competitors rank for.
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'technical' && (
          <div className="space-y-6">
            {/* Technical Score */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className={`${getScoreBg(seoData?.technicalSeo?.score)} rounded-2xl p-6`}>
                <div className="text-sm font-medium text-slate-600 mb-2">Technical SEO Score</div>
                <div className={`text-4xl font-bold ${getScoreColor(seoData?.technicalSeo?.score)}`}>
                  {seoData?.technicalSeo?.score}/100
                </div>
              </div>
              
              <div className="bg-green-50 rounded-2xl p-6">
                <div className="text-sm font-medium text-slate-600 mb-2">Checks Passed</div>
                <div className="text-4xl font-bold text-green-600">
                  {seoData?.technicalSeo?.passed?.length || 0}
                </div>
              </div>
              
              <div className="bg-amber-50 rounded-2xl p-6">
                <div className="text-sm font-medium text-slate-600 mb-2">Issues Found</div>
                <div className="text-4xl font-bold text-amber-600">
                  {seoData?.technicalSeo?.issues?.length || 0}
                </div>
              </div>
            </div>

            {/* Passed Checks */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                Passed Checks
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                {seoData?.technicalSeo?.passed?.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-slate-700">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    {item}
                  </div>
                ))}
              </div>
            </div>

            {/* Issues */}
            {seoData?.technicalSeo?.issues?.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Issues to Address
                </h3>
                <div className="space-y-3">
                  {seoData?.technicalSeo?.issues?.map((issue, idx) => (
                    <div key={idx} className={`flex items-start gap-3 p-3 rounded-lg ${
                      issue.type === 'warning' ? 'bg-amber-50' : 'bg-blue-50'
                    }`}>
                      {issue.type === 'warning' ? (
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      ) : (
                        <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      )}
                      <div className={issue.type === 'warning' ? 'text-amber-800' : 'text-blue-800'}>
                        {issue.message}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminSidebar>
  );
};

export default AdminSeoPage;
